import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getLead, getUsers, convertLead, getLeadActivities, createLeadActivity, getLeadNotes, createLeadNote, updateLeadNote, getLeadFollowUps, createLeadFollowUp, getLeadTasks, createLeadTask, getTimeline } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatPhone, getStatusBadgeClass, formatApiError } from '../../lib/utils';
import { LEAD_STATUSES, CLOSE_REASONS, ACTIVITY_TYPES, FOLLOW_UP_TYPES, TASK_TYPES, PRIORITIES, TASK_CATEGORY_MAP, LEAD_FOLLOW_UP_TYPES, TIME_SLOTS } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { DatePicker } from '../../components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import LeadStatusFollowUpDialog, { defaultFollowUpForStatus } from './LeadStatusFollowUpDialog';
import WhatsAppNudgeDialog, { WHATSAPP_NUDGE_REASONS } from './WhatsAppNudgeDialog';
import {
  ArrowLeft, Pencil, ArrowsClockwise, Phone, Envelope, Tag, CalendarBlank,
  Plus, Clock, ChatCircleText, ClipboardText, ListChecks, Note, UserSwitch
} from '@phosphor-icons/react';

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');

  // Data
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeline, setTimeline] = useState([]);

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [postStatusFollowUp, setPostStatusFollowUp] = useState({ open: false, status: '' });
  const [waNudgeOpen, setWaNudgeOpen] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [converting, setConverting] = useState(false);

  // Forms
  const [statusForm, setStatusForm] = useState({ status: '', close_reason: '', close_reason_other: '' });
  const [activityForm, setActivityForm] = useState({ activity_type: '', date: '', time: '', outcome_notes: '' });
  const [noteContent, setNoteContent] = useState('');
  const [followUpForm, setFollowUpForm] = useState({ follow_up_type: '', due_date: '', due_time: '', priority: 'Medium', description: '' });
  const [taskForm, setTaskForm] = useState({ task_type: '', due_date: '', priority: 'Medium', description: '' });
  const [convertForm, setConvertForm] = useState({
    client_type: 'Individual', full_name: '', phone: '', email: '',
    address: '', city: '', state: '', pin_code: '',
    tax_status: 'Resident Individual', kyc_acquisition_type: 'Fresh KYC', kyc_status: 'KYC Pending'
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [leadRes, usersRes] = await Promise.all([getLead(id), getUsers()]);
        setLead(leadRes.data);
        const usersMap = {};
        usersRes.data.forEach(u => { usersMap[u.id] = u.name; });
        setUsers(usersMap);

        // Fetch tab data in parallel
        const [actRes, notesRes, fuRes, tasksRes, tlRes] = await Promise.all([
          getLeadActivities(id).catch(() => ({ data: [] })),
          getLeadNotes(id).catch(() => ({ data: [] })),
          getLeadFollowUps(id).catch(() => ({ data: [] })),
          getLeadTasks(id).catch(() => ({ data: [] })),
          getTimeline('lead', id).catch(() => ({ data: [] }))
        ]);
        setActivities(actRes.data);
        setNotes(notesRes.data);
        setFollowUps(fuRes.data);
        setTasks(tasksRes.data);
        setTimeline(tlRes.data);
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- refetches on lead id change only

  const handleStatusUpdate = async () => {
    try {
      await api.patch(`/leads/${id}/status`, statusForm);
      const newSt = statusForm.status;
      const reason = statusForm.close_reason;
      setLead(prev => ({
        ...prev,
        status: newSt,
        close_reason: newSt === 'Closed' ? reason : null,
        close_reason_other: newSt === 'Closed' && reason === 'Other' ? statusForm.close_reason_other : null,
      }));
      setShowStatusModal(false);
      toast.success(`Status updated to ${newSt}`);
      // Refresh timeline
      const tlRes = await getTimeline('lead', id);
      setTimeline(tlRes.data);
      // Open follow-up dialog if this status has defaults
      if (defaultFollowUpForStatus(newSt)) {
        setPostStatusFollowUp({ open: true, status: newSt });
      }
      // WhatsApp nudge for soft-closure reasons
      if (newSt === 'Closed' && WHATSAPP_NUDGE_REASONS.has(reason)) {
        setWaNudgeOpen(true);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      const res = await createLeadActivity({ ...activityForm, lead_id: id });
      setActivities([res.data, ...activities]);
      setShowActivityModal(false);
      setActivityForm({ activity_type: '', date: '', time: '', outcome_notes: '' });
      toast.success('Activity logged');
      const tlRes = await getTimeline('lead', id);
      setTimeline(tlRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const res = await createLeadNote({ lead_id: id, content: noteContent });
      setNotes([res.data, ...notes]);
      setShowNoteModal(false);
      setNoteContent('');
      toast.success('Note added');
      const tlRes = await getTimeline('lead', id);
      setTimeline(tlRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      const res = await updateLeadNote(editingNote.id, { content: noteContent });
      setNotes(notes.map(n => n.id === editingNote.id ? { ...n, content: noteContent, updated_at: new Date().toISOString() } : n));
      setEditingNote(null);
      setNoteContent('');
      toast.success('Note updated');
      const tlRes = await getTimeline('lead', id);
      setTimeline(tlRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    try {
      const res = await createLeadFollowUp({ ...followUpForm, lead_id: id });
      setFollowUps([res.data, ...followUps]);
      setShowFollowUpModal(false);
      setFollowUpForm({ follow_up_type: '', due_date: '', due_time: '', priority: 'Medium', description: '' });
      toast.success('Follow-up created');
      const tlRes = await getTimeline('lead', id);
      setTimeline(tlRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const res = await createLeadTask({ ...taskForm, lead_id: id });
      setTasks([res.data, ...tasks]);
      setShowTaskModal(false);
      setTaskForm({ task_type: '', due_date: '', priority: 'Medium', description: '' });
      toast.success('Task created');
      const tlRes = await getTimeline('lead', id);
      setTimeline(tlRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openConvertModal = () => {
    setConvertForm({
      client_type: 'Individual',
      full_name: lead.full_name || lead.company_name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      address: '', city: '', state: '', pin_code: '',
      tax_status: 'Resident Individual',
      kyc_acquisition_type: 'Fresh KYC',
      kyc_status: 'KYC Pending'
    });
    setShowConvertModal(true);
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    setConverting(true);
    try {
      const res = await convertLead(id, convertForm);
      toast.success('Lead converted to client successfully!');
      navigate(`/clients/${res.data.id}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setConverting(false);
    }
  };

  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-[600px]" />
    </div>
  );

  if (!lead) return <div className="p-6">Lead not found</div>;

  const getEventIcon = (type) => {
    switch (type) {
      case 'status_change': return <ArrowsClockwise size={16} className="text-blue-500" />;
      case 'activity_logged': return <Phone size={16} className="text-green-500" />;
      case 'note_added': case 'note_updated': return <Note size={16} className="text-amber-500" />;
      case 'follow_up_added': return <CalendarBlank size={16} className="text-purple-500" />;
      case 'task_added': return <ClipboardText size={16} className="text-cyan-500" />;
      default: return <Clock size={16} className="text-zinc-400" />;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/leads')} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {lead.company_name || lead.full_name}
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <Badge className={`${getStatusBadgeClass(lead.status)} text-xs`}>{lead.status}</Badge>
              {lead.status === 'Closed' && lead.close_reason && (
                <span className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-sm" data-testid="close-reason-header">
                  Closed: {lead.close_reason === 'Other' && lead.close_reason_other ? `Other — ${lead.close_reason_other}` : lead.close_reason}
                </span>
              )}
              <span className="text-sm text-zinc-500">{lead.source}</span>
              {lead.campaign_name && <span className="text-sm text-zinc-500">/ {lead.campaign_name}</span>}
              {lead.company_name && lead.full_name && (
                <span className="text-sm text-zinc-500">({lead.full_name})</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Upfront Status Update Button */}
          <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
            <DialogTrigger asChild>
              <Button
                variant="outline" className="rounded-sm"
                onClick={() => setStatusForm({ status: lead.status, close_reason: '', close_reason_other: '' })}
                data-testid="update-status-button"
              >
                <ArrowsClockwise size={16} className="mr-2" /> Update Status
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Update Lead Status</DialogTitle></DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">New Status</Label>
                  <SearchableSelect
                    value={statusForm.status}
                    onValueChange={(v) => setStatusForm(prev => ({ ...prev, status: v }))}
                    options={LEAD_STATUSES}
                    placeholder="Select status"
                    data-testid="status-select"
                  />
                </div>
                {statusForm.status === 'Closed' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-semibold">Close Reason *</Label>
                    <SearchableSelect
                      value={statusForm.close_reason}
                      onValueChange={(v) => setStatusForm(prev => ({ ...prev, close_reason: v }))}
                      options={CLOSE_REASONS}
                      placeholder="Select reason"
                    />
                  </div>
                )}
                {statusForm.close_reason === 'Other' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-semibold">Specify Reason</Label>
                    <Input
                      value={statusForm.close_reason_other}
                      onChange={(e) => setStatusForm(prev => ({ ...prev, close_reason_other: e.target.value }))}
                      className="rounded-sm"
                    />
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleStatusUpdate}
                    disabled={statusForm.status === lead.status || (statusForm.status === 'Closed' && !statusForm.close_reason)}
                    className="bg-[#002FA7] text-white"
                    data-testid="confirm-status-button"
                  >
                    Update Status
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {lead.status !== 'Converted' && lead.status !== 'Closed' && (
            <Button
              variant="outline"
              className="rounded-sm border-green-500 text-green-700 hover:bg-green-50"
              onClick={openConvertModal}
              data-testid="convert-lead-button"
            >
              <UserSwitch size={16} className="mr-2" /> Convert to Client
            </Button>
          )}

          <Button variant="outline" onClick={() => navigate(`/leads/${id}/edit`)} className="rounded-sm" data-testid="edit-lead-button">
            <Pencil size={16} className="mr-2" /> Edit
          </Button>
        </div>
      </div>

      {/* Convert to Client Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Convert Lead to Client</DialogTitle></DialogHeader>
          <form onSubmit={handleConvert} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase">Client Type *</Label>
                <SearchableSelect
                  value={convertForm.client_type}
                  onValueChange={(v) => setConvertForm(p => ({ ...p, client_type: v }))}
                  options={['Individual', 'HUF', 'Firm', 'Company', 'Trust', 'NRI']}
                  placeholder="Select type"
                  data-testid="convert-client-type"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Full Name *</Label>
                <Input value={convertForm.full_name} onChange={(e) => setConvertForm(p => ({ ...p, full_name: e.target.value }))} required className="rounded-sm" data-testid="convert-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Phone</Label>
                <Input value={convertForm.phone} onChange={(e) => setConvertForm(p => ({ ...p, phone: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Email</Label>
                <Input value={convertForm.email} onChange={(e) => setConvertForm(p => ({ ...p, email: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase">Address *</Label>
                <Input value={convertForm.address} onChange={(e) => setConvertForm(p => ({ ...p, address: e.target.value }))} required className="rounded-sm" data-testid="convert-address" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">City *</Label>
                <Input value={convertForm.city} onChange={(e) => setConvertForm(p => ({ ...p, city: e.target.value }))} required className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">State *</Label>
                <Input value={convertForm.state} onChange={(e) => setConvertForm(p => ({ ...p, state: e.target.value }))} required className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Pin Code *</Label>
                <Input value={convertForm.pin_code} onChange={(e) => setConvertForm(p => ({ ...p, pin_code: e.target.value }))} required className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Tax Status *</Label>
                <SearchableSelect
                  value={convertForm.tax_status}
                  onValueChange={(v) => setConvertForm(p => ({ ...p, tax_status: v }))}
                  options={['Resident Individual', 'NRI', 'Minor', 'Company', 'Partnership']}
                  placeholder="Select tax status"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">KYC Status</Label>
                <SearchableSelect
                  value={convertForm.kyc_status}
                  onValueChange={(v) => setConvertForm(p => ({ ...p, kyc_status: v }))}
                  options={['KYC Done', 'KYC Pending', 'KYC Rejected', 'KYC Expired']}
                  placeholder="Select KYC status"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowConvertModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" disabled={converting} data-testid="confirm-convert-button">
                {converting ? 'Converting...' : 'Convert to Client'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Info Summary */}
      <Card className="border-zinc-200 rounded-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {lead.company_name && (
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Company</p>
                <p className="font-medium">{lead.company_name}</p>
              </div>
            )}
            {lead.full_name && (
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Contact Person</p>
                <p>{lead.full_name}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Phone</p>
              <p className="flex items-center gap-2">
                <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-[#002FA7] hover:underline" data-testid="lead-call-primary"><Phone size={16} /> {formatPhone(lead.phone)}</a>
                <a href={`https://wa.me/${(lead.phone || '').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline text-xs uppercase">WhatsApp</a>
              </p>
            </div>
            {lead.secondary_phone && (
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Secondary Phone</p>
                <p className="flex items-center gap-2">
                  <a href={`tel:${lead.secondary_phone}`} className="flex items-center gap-1.5 text-[#002FA7] hover:underline" data-testid="lead-call-secondary"><Phone size={16} /> {formatPhone(lead.secondary_phone)}</a>
                  <a href={`https://wa.me/${(lead.secondary_phone || '').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline text-xs uppercase">WhatsApp</a>
                </p>
              </div>
            )}
            {lead.tertiary_phone && (
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Tertiary Phone</p>
                <p className="flex items-center gap-2">
                  <a href={`tel:${lead.tertiary_phone}`} className="flex items-center gap-1.5 text-[#002FA7] hover:underline" data-testid="lead-call-tertiary"><Phone size={16} /> {formatPhone(lead.tertiary_phone)}</a>
                  <a href={`https://wa.me/${(lead.tertiary_phone || '').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline text-xs uppercase">WhatsApp</a>
                </p>
              </div>
            )}
            {lead.address && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Address</p>
                <p className="text-sm">{lead.address}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Email</p>
              <p className="flex items-center gap-2"><Envelope size={16} className="text-zinc-400" /> {lead.email || '-'}</p>
            </div>
            {lead.dob && (
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Date of Birth</p>
                <p data-testid="lead-dob">{formatDate(lead.dob)}</p>
              </div>
            )}
            {lead.member_id && (
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Member ID</p>
                <p className="tabular-nums" data-testid="lead-member-id">{lead.member_id}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Assigned To</p>
              <p>{users[lead.assigned_to] || '-'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Created</p>
              <p>{formatDate(lead.created_at)}</p>
            </div>
            {lead.service_interest?.length > 0 && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-2">Service Interests</p>
                <div className="flex flex-wrap gap-2">
                  {lead.service_interest.map(s => (
                    <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
            {lead.notes && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Notes</p>
                <p className="text-sm whitespace-pre-line bg-zinc-50 p-3 rounded-sm">{lead.notes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList className="bg-zinc-100 rounded-sm">
            <TabsTrigger value="timeline" className="rounded-sm text-xs data-[state=active]:bg-white">
              <Clock size={14} className="mr-1.5" /> Timeline
            </TabsTrigger>
            <TabsTrigger value="activities" className="rounded-sm text-xs data-[state=active]:bg-white">
              <Phone size={14} className="mr-1.5" /> Activities ({activities.length})
            </TabsTrigger>
            <TabsTrigger value="notes" className="rounded-sm text-xs data-[state=active]:bg-white">
              <ChatCircleText size={14} className="mr-1.5" /> Notes ({notes.length})
            </TabsTrigger>
            <TabsTrigger value="follow-ups" className="rounded-sm text-xs data-[state=active]:bg-white">
              <CalendarBlank size={14} className="mr-1.5" /> Follow-ups ({followUps.length})
            </TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-sm text-xs data-[state=active]:bg-white">
              <ListChecks size={14} className="mr-1.5" /> Tasks ({tasks.length})
            </TabsTrigger>
          </TabsList>

          {/* Quick Add Buttons */}
          <div className="flex gap-2">
            {activeTab === 'activities' && (
              <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#002FA7] text-white rounded-sm" data-testid="add-activity-button">
                    <Plus size={14} className="mr-1" /> Log Activity
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateActivity} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Type *</Label>
                        <SearchableSelect
                          value={activityForm.activity_type}
                          onValueChange={(v) => setActivityForm(p => ({ ...p, activity_type: v }))}
                          options={ACTIVITY_TYPES}
                          placeholder="Select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Date *</Label>
                        <DatePicker value={activityForm.date} onChange={(v) => setActivityForm(p => ({ ...p, date: v }))} maxDate={new Date().toISOString().split('T')[0]} placeholder="Select date" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Time *</Label>
                        <SearchableSelect
                          value={activityForm.time || ''}
                          onValueChange={(v) => setActivityForm(p => ({ ...p, time: v }))}
                          options={TIME_SLOTS.map(s => ({ value: s.value, label: s.label }))}
                          placeholder="Select time"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Notes</Label>
                      <Textarea value={activityForm.outcome_notes} onChange={(e) => setActivityForm(p => ({ ...p, outcome_notes: e.target.value }))} className="rounded-sm" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white">Log Activity</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === 'notes' && (
              <Dialog open={showNoteModal || !!editingNote} onOpenChange={(open) => { if (!open) { setShowNoteModal(false); setEditingNote(null); setNoteContent(''); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#002FA7] text-white rounded-sm" data-testid="add-note-button" onClick={() => { setNoteContent(''); setEditingNote(null); setShowNoteModal(true); }}>
                    <Plus size={14} className="mr-1" /> Add Note
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{editingNote ? 'Edit Note' : 'Add Note'}</DialogTitle></DialogHeader>
                  <form onSubmit={editingNote ? handleUpdateNote : handleCreateNote} className="space-y-4 py-4">
                    <Textarea
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      required placeholder="Write your note..."
                      className="rounded-sm min-h-[120px]"
                      data-testid="note-content-input"
                    />
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => { setShowNoteModal(false); setEditingNote(null); }}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white" data-testid="save-note-button">{editingNote ? 'Update' : 'Add'} Note</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === 'follow-ups' && (
              <Dialog open={showFollowUpModal} onOpenChange={setShowFollowUpModal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#002FA7] text-white rounded-sm" data-testid="add-followup-button">
                    <Plus size={14} className="mr-1" /> Add Follow-up
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Follow-up</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateFollowUp} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Type *</Label>
                        <SearchableSelect
                          value={followUpForm.follow_up_type}
                          onValueChange={(v) => setFollowUpForm(p => ({ ...p, follow_up_type: v }))}
                          options={LEAD_FOLLOW_UP_TYPES}
                          placeholder="Select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Priority</Label>
                        <SearchableSelect
                          value={followUpForm.priority}
                          onValueChange={(v) => setFollowUpForm(p => ({ ...p, priority: v }))}
                          options={PRIORITIES}
                          placeholder="Select priority"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Due Date *</Label>
                        <DatePicker value={followUpForm.due_date} onChange={(v) => setFollowUpForm(p => ({ ...p, due_date: v }))} placeholder="Select due date" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Due Time</Label>
                        <SearchableSelect
                          value={followUpForm.due_time || ''}
                          onValueChange={(v) => setFollowUpForm(p => ({ ...p, due_time: v }))}
                          options={TIME_SLOTS.map(s => ({ value: s.value, label: s.label }))}
                          placeholder="Select time"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Description</Label>
                      <Textarea value={followUpForm.description} onChange={(e) => setFollowUpForm(p => ({ ...p, description: e.target.value }))} className="rounded-sm" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowFollowUpModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white">Create Follow-up</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === 'tasks' && (
              <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#002FA7] text-white rounded-sm" data-testid="add-task-button">
                    <Plus size={14} className="mr-1" /> Add Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Task</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateTask} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Type *</Label>
                        <SearchableSelect
                          value={taskForm.task_type}
                          onValueChange={(v) => setTaskForm(p => ({ ...p, task_type: v }))}
                          groups={Object.entries(TASK_CATEGORY_MAP).map(([cat, types]) => ({ label: cat, options: types }))}
                          placeholder="Select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Priority</Label>
                        <SearchableSelect
                          value={taskForm.priority}
                          onValueChange={(v) => setTaskForm(p => ({ ...p, priority: v }))}
                          options={PRIORITIES}
                          placeholder="Select priority"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Due Date *</Label>
                        <DatePicker value={taskForm.due_date} onChange={(v) => setTaskForm(p => ({ ...p, due_date: v }))} placeholder="Select due date" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Description</Label>
                      <Textarea value={taskForm.description} onChange={(e) => setTaskForm(p => ({ ...p, description: e.target.value }))} className="rounded-sm" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white">Create Task</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100">
              <CardTitle className="text-base">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {timeline.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No events yet. Log activities, notes, follow-ups or update status to see the timeline.</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-zinc-200" />
                  <div className="space-y-6">
                    {timeline.map((event) => (
                      <div key={event.id} className="relative flex items-start gap-4 pl-10" data-testid={`timeline-event-${event.id}`}>
                        <div className="absolute left-2 top-1 w-5 h-5 rounded-full bg-white border-2 border-zinc-200 flex items-center justify-center">
                          {getEventIcon(event.event_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{event.description}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-zinc-500">{event.created_by_name || 'System'}</span>
                            <span className="text-xs text-zinc-400">{new Date(event.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {event.event_type.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status History */}
          {lead.status_history?.length > 0 && (
            <Card className="border-zinc-200 rounded-sm mt-6">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="text-base">Status History</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {lead.status_history.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm" data-testid={`status-history-${i}`}>
                      <Badge className={`${getStatusBadgeClass(h.from_status)} text-[10px]`}>{h.from_status}</Badge>
                      <span className="text-zinc-400">→</span>
                      <Badge className={`${getStatusBadgeClass(h.to_status)} text-[10px]`}>{h.to_status}</Badge>
                      <span className="text-xs text-zinc-500 ml-auto">{h.changed_by_name} · {new Date(h.changed_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-6">
              {activities.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No activities logged yet.</p>
              ) : (
                <div className="space-y-4">
                  {activities.map(a => (
                    <div key={a.id} className="flex items-start gap-4 p-4 border border-zinc-100 rounded-sm" data-testid={`activity-${a.id}`}>
                      <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                        <Phone size={18} className="text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{a.activity_type}</Badge>
                          <span className="text-xs text-zinc-500">{a.date} {a.time}</span>
                        </div>
                        {a.outcome_notes && <p className="text-sm mt-1 text-zinc-700">{a.outcome_notes}</p>}
                        <p className="text-xs text-zinc-400 mt-1">By {a.logged_by_name || users[a.logged_by] || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-6">
              {notes.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No notes yet.</p>
              ) : (
                <div className="space-y-4">
                  {notes.map(n => (
                    <div key={n.id} className="p-4 border border-zinc-100 rounded-sm" data-testid={`note-${n.id}`}>
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-zinc-700 whitespace-pre-wrap">{n.content}</p>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => { setEditingNote(n); setNoteContent(n.content); }}
                          data-testid={`edit-note-${n.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-zinc-500">{n.created_by_name || '-'}</span>
                        <span className="text-xs text-zinc-400">{new Date(n.created_at).toLocaleString()}</span>
                        {n.updated_at !== n.created_at && (
                          <span className="text-xs text-zinc-400 italic">(edited)</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow-ups Tab */}
        <TabsContent value="follow-ups" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-6">
              {followUps.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No follow-ups yet.</p>
              ) : (
                <div className="space-y-4">
                  {followUps.map(fu => (
                    <div key={fu.id} className="flex items-start gap-4 p-4 border border-zinc-100 rounded-sm" data-testid={`followup-${fu.id}`}>
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                        <CalendarBlank size={18} className="text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{fu.follow_up_type}</span>
                          <Badge className={`text-xs ${fu.priority === 'High' ? 'bg-red-100 text-red-700' : fu.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-700'}`}>
                            {fu.priority}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{fu.status}</Badge>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">Due: {fu.due_date} {fu.due_time || ''}</p>
                        {fu.description && <p className="text-sm mt-1 text-zinc-600">{fu.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardContent className="p-6">
              {tasks.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No tasks yet.</p>
              ) : (
                <div className="space-y-4">
                  {tasks.map(t => (
                    <div key={t.id} className="flex items-start gap-4 p-4 border border-zinc-100 rounded-sm" data-testid={`task-${t.id}`}>
                      <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center shrink-0">
                        <ClipboardText size={18} className="text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{t.title}</span>
                          <Badge variant="outline" className="text-xs">{t.task_type}</Badge>
                          <Badge className={`text-xs ${t.priority === 'High' ? 'bg-red-100 text-red-700' : t.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-700'}`}>
                            {t.priority}
                          </Badge>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">Due: {t.due_date} · Status: {t.status}</p>
                        {t.description && <p className="text-sm mt-1 text-zinc-600">{t.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Auto-suggest follow-up after a status change */}
      <LeadStatusFollowUpDialog
        open={postStatusFollowUp.open}
        onOpenChange={(v) => setPostStatusFollowUp((p) => ({ ...p, open: v }))}
        lead={lead}
        newStatus={postStatusFollowUp.status}
        statusAlreadyPersisted
        onUpdated={async ({ follow_up_created }) => {
          if (follow_up_created) {
            try {
              const fuRes = await getLeadFollowUps(id);
              setFollowUps(fuRes.data || []);
              const tlRes = await getTimeline('lead', id);
              setTimeline(tlRes.data);
            } catch (e) { /* silent */ }
          }
        }}
      />

      {/* WhatsApp nudge after soft-closure */}
      <WhatsAppNudgeDialog
        open={waNudgeOpen}
        onOpenChange={setWaNudgeOpen}
        leadId={lead?.id}
        leadName={lead?.full_name || lead?.company_name}
        leadPhone={lead?.phone}
        closeReason={lead?.close_reason}
      />
    </div>
  );
}
